// ============================================================
// PREDICTIVE ENGINE — Fase 7.7
// Motor de previsão operacional baseado em regras + heurísticas
// Produz insights acionáveis com probabilidade e horizonte temporal
// ============================================================
import type { RiskContext, RiskAssessment } from '@/types/operational'
import type { OperationalInsight, InsightType } from '@/types/ai'

let counter = 0
const mkId = (t: string) => `${t}-${Date.now()}-${++counter}`
const TTL_1H  = 60 * 60 * 1000
const TTL_4H  = 4 * TTL_1H
const TTL_24H = 24 * TTL_1H

// ─── Insight builders ─────────────────────────────────────────
function weatherImpact(ctx: RiskContext): OperationalInsight[] {
  const insights: OperationalInsight[] = []
  const clima = ctx.clima
  if (!clima) return insights

  // Chuva → impacto em concreto e alvenaria
  if (clima.chuva && clima.precipitacaoMm > 5) {
    const hasConcr = ctx.concretagem?.ativa
    const prob = Math.min(0.95, 0.5 + clima.precipitacaoMm / 50)
    insights.push({
      id:          mkId('weather_rain'),
      type:        'weather_impact',
      severity:    hasConcr ? 'critical' : 'warning',
      title:       hasConcr
        ? '⚠ Chuva compromete concretagem em andamento'
        : `⚠ Chuva ${clima.precipitacaoMm}mm pode impactar serviços descobertos`,
      description: hasConcr
        ? `Precipitação de ${clima.precipitacaoMm}mm detectada. Relação a/c afetada. Acionar responsável técnico.`
        : `Alvenaria, argamassa e serviços externos podem ser paralisados. Avaliar jornada de trabalho.`,
      probability:  prob,
      impactScore:  hasConcr ? 90 : 55,
      timeHorizon:  'agora',
      actions: [
        { label: hasConcr ? 'Acionar responsável' : 'Ver RDO', type: 'navigate' },
      ],
      context:   { precipitacao: clima.precipitacaoMm, concretagem: hasConcr },
      createdAt: Date.now(),
      expiresAt: Date.now() + TTL_4H,
    })
  }

  // Temperatura extrema
  if (clima.temperatura >= 35 || clima.temperatura <= 5) {
    const hot = clima.temperatura >= 35
    insights.push({
      id:          mkId('weather_temp'),
      type:        'weather_impact',
      severity:    'warning',
      title:       hot
        ? `⚠ Temperatura ${clima.temperatura}°C — risco de estresse térmico`
        : `⚠ Temperatura ${clima.temperatura}°C — risco de congelamento`,
      description: hot
        ? 'Hidratação obrigatória e pausas a cada 45min. Avaliar trabalho pesado ao ar livre.'
        : 'Proteger concretagens e argamassas. Verificar EPIs adequados.',
      probability:  0.9,
      impactScore:  60,
      timeHorizon:  'agora',
      actions: [{ label: 'Ver checklist SST', type: 'navigate' }],
      context:   { temperatura: clima.temperatura },
      createdAt: Date.now(),
      expiresAt: Date.now() + TTL_4H,
    })
  }

  return insights
}

function productionPrediction(ctx: RiskContext): OperationalInsight[] {
  const insights: OperationalInsight[] = []
  const prod = ctx.producao

  // Efetivo insuficiente para meta
  if (prod.efetivoMeta > 0) {
    const efPct = prod.efetivoPresente / prod.efetivoMeta
    if (efPct < 0.8) {
      const desvioProducao = Math.round((1 - efPct) * 100)
      insights.push({
        id:         mkId('workforce_pred'),
        type:       'workforce_prediction',
        severity:   efPct < 0.6 ? 'critical' : 'warning',
        title:      `⚠ Efetivo insuficiente para meta ${efPct < 0.6 ? 'semanal' : 'diária'}`,
        description: `${prod.efetivoPresente} de ${prod.efetivoMeta} trabalhadores. Produção estimada ${desvioProducao}% abaixo do previsto.`,
        probability: 0.85,
        impactScore: Math.round((1 - efPct) * 100),
        timeHorizon: 'hoje',
        actions: [
          { label: 'Acionar empreiteiras', type: 'alert' },
          { label: 'Ver efetivo', type: 'navigate' },
        ],
        context:   { efetivo: prod.efetivoPresente, meta: prod.efetivoMeta, pct: efPct },
        createdAt: Date.now(),
        expiresAt: Date.now() + TTL_4H,
      })
    }
  }

  // Atraso acumulado → previsão de prazo
  if (prod.avancoPrevisto > 0) {
    const desvio = prod.avancoRealizado - prod.avancoPrevisto
    if (desvio < -10) {
      const diasAtraso = Math.round(Math.abs(desvio) / 0.5) // heuristic ~0.5% per day
      insights.push({
        id:         mkId('delay_pred'),
        type:       'predictive_delay',
        severity:   desvio < -20 ? 'critical' : 'warning',
        title:      `⚠ Alta probabilidade de atraso — ${diasAtraso} dias estimados`,
        description: `Avanço real ${prod.avancoRealizado.toFixed(1)}% vs ${prod.avancoPrevisto.toFixed(1)}% previsto. Tendência de entrega atrasada.`,
        probability: Math.min(0.95, 0.5 + Math.abs(desvio) / 40),
        impactScore: Math.min(100, 50 + Math.abs(desvio)),
        timeHorizon: `${diasAtraso} dias`,
        actions: [
          { label: 'Ver curva S', type: 'navigate' },
          { label: 'Escalar para diretor', type: 'escalate' },
        ],
        context:   { realizado: prod.avancoRealizado, previsto: prod.avancoPrevisto, diasAtraso },
        createdAt: Date.now(),
        expiresAt: Date.now() + TTL_24H,
      })
    }
  }

  return insights
}

function supplyRisk(ctx: RiskContext): OperationalInsight[] {
  const insights: OperationalInsight[] = []
  const forn = ctx.fornecedores

  if (forn.criticos > 0) {
    const temConcretagem = ctx.concretagem &&
      (ctx.concretagem.ativa ||
       new Date(ctx.concretagem.data).getTime() - Date.now() < 48 * 3600_000)

    if (temConcretagem) {
      insights.push({
        id:         mkId('supply_concr'),
        type:       'supply_shortage',
        severity:   'critical',
        title:      '⚠ Risco de ruptura de material crítico para concretagem',
        description: `${forn.criticos} material(is) em estoque crítico com concretagem planejada. Acionar compras emergencialmente.`,
        probability: 0.9,
        impactScore: 95,
        timeHorizon: '48h',
        actions: [
          { label: 'Abrir pedido emergencial', type: 'alert' },
          { label: 'Ver suprimentos', type: 'navigate' },
        ],
        context:   { criticos: forn.criticos, concretagem: ctx.concretagem?.data },
        createdAt: Date.now(),
        expiresAt: Date.now() + TTL_4H,
      })
    } else {
      insights.push({
        id:         mkId('supply_risk'),
        type:       'supplier_risk',
        severity:   'warning',
        title:      `⚠ Risco de ruptura hidráulica em ${forn.criticos > 1 ? 'materiais críticos' : 'material crítico'}`,
        description: `${forn.criticos} item(ns) com menos de 3 dias de estoque. Sem reposição emergencial, serviços podem parar.`,
        probability: 0.75,
        impactScore: 70,
        timeHorizon: '2-3 dias',
        actions: [
          { label: 'Ver estoque crítico', type: 'navigate' },
        ],
        context:   { criticos: forn.criticos },
        createdAt: Date.now(),
        expiresAt: Date.now() + TTL_24H,
      })
    }
  }

  return insights
}

function safetyPrediction(ctx: RiskContext): OperationalInsight[] {
  const insights: OperationalInsight[] = []
  const sst = ctx.sst

  // Dias sem acidente decreasing trend warning (if < 7 days)
  if (sst.diasSemAcidente < 7 && sst.ocorrenciasCriticas === 0) {
    insights.push({
      id:         mkId('safety_streak'),
      type:       'risk_escalation',
      severity:   'warning',
      title:      `⚠ Apenas ${sst.diasSemAcidente} dias sem acidente`,
      description: 'Histórico recente de incidentes. Reforçar DDS, verificar EPIs e áreas bloqueadas.',
      probability: 0.6,
      impactScore: 65,
      timeHorizon: 'hoje',
      actions: [{ label: 'Agendar DDS emergencial', type: 'alert' }],
      context:   { diasSemAcidente: sst.diasSemAcidente },
      createdAt: Date.now(),
      expiresAt: Date.now() + TTL_24H,
    })
  }

  return insights
}

// ─── Main predictive engine ───────────────────────────────────
export async function runPredictiveEngine(
  ctx:        RiskContext,
  assessment: RiskAssessment,
): Promise<OperationalInsight[]> {
  // All generators run in parallel
  const all = [
    ...weatherImpact(ctx),
    ...productionPrediction(ctx),
    ...supplyRisk(ctx),
    ...safetyPrediction(ctx),
  ]

  // Filter out low-impact insights when overall risk is low
  const threshold = assessment.score < 30 ? 0.6 : 0.4
  const filtered  = all.filter(i => i.probability >= threshold)

  // Sort by impact × probability
  return filtered.sort((a, b) =>
    (b.impactScore * b.probability) - (a.impactScore * a.probability)
  )
}
